// Fixed AddReferralCodeEnhanced.js

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';
import { validateReferralCode } from '../utils/referralSystem';

const AddReferralCodeEnhanced = ({ userId, userDetails, onSuccess }) => {
  const [referralCode, setReferralCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationResult, setValidationResult] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);

  // Check if user already has a referral
  const userHasReferral = userDetails?.referredBy || userDetails?.hasReferralCode;
  const pendingReferralCode = userDetails?.pendingReferralCode;

  useEffect(() => {
    // Pre-fill if they entered a code during registration
    if (pendingReferralCode && !userHasReferral) {
      setReferralCode(pendingReferralCode);
      setShowForm(true);
    }
  }, [pendingReferralCode, userHasReferral]);

  const handleValidate = async () => {
    if (!referralCode || referralCode.trim().length < 6) {
      setError('Please enter a valid referral code');
      return;
    }

    setIsValidating(true);
    setError('');
    setValidationResult(null);

    try {
      const validation = await validateReferralCode(referralCode.trim());
      
      if (validation.valid) {
        // Make sure user isn't trying to use their own code
        if (validation.referrerId === userId) {
          setError("You cannot use your own referral code");
          setValidationResult(null);
          return;
        }
        
        setValidationResult(validation);
        setError('');
      } else {
        setError(validation.error || 'Invalid referral code');
        setValidationResult(null);
      }
    } catch (error) {
      console.error('Error validating referral code:', error);
      setError('Error validating referral code');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async () => {
    if (!validationResult || !validationResult.valid) {
      setError('Please validate the referral code first');
      return;
    }

    setIsSubmitting(true);
    setError('');
    setSuccess('');

    try {
      // FIX: Use the referralCode from state (the one user entered), not from validationResult
      const cleanedReferralCode = referralCode.trim().toUpperCase();
      
      // Update the user's document with referral information
      await updateDoc(doc(db, 'Investors', userId), {
        referredBy: validationResult.referrerId,
        referredByCode: cleanedReferralCode, // ✅ FIXED: Use the actual code entered
        referredByName: validationResult.referrerName,
        referralDate: serverTimestamp(),
        referralBonusProcessed: false,
        referralProcessingStatus: 'pending',
        hasReferralCode: true,
        pendingReferralCode: null, // Clear the pending code
        referralAddedAt: serverTimestamp()
      });

      setSuccess(`Success! You were referred by ${validationResult.referrerName}. Your referral bonus will be processed by our admin team.`);
      
      if (onSuccess) {
        onSuccess();
      }

      // Hide form after success
      setTimeout(() => {
        setShowForm(false);
      }, 3000);
      
    } catch (error) {
      console.error('Error processing referral:', error);
      setError(error.message || 'Failed to process referral. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // If user already has a referral, show success state
  if (userHasReferral) {
    return (
      <motion.div 
        className="bg-gradient-to-r from-green-500/20 to-emerald-600/20 backdrop-blur-md rounded-xl p-6 border border-green-500/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <span className="text-2xl mr-3">✅</span>
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-white">
              Referral Active
            </h3>
            <p className="mt-1 text-sm text-gray-300">
              You were referred by <span className="text-green-400 font-semibold">{userDetails.referredByName}</span>
            </p>
            {!userDetails.referralBonusProcessed && (
              <p className="mt-1 text-xs text-yellow-400">
                Bonus pending admin approval
              </p>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="bg-gradient-to-r from-yellow-500/20 to-orange-600/20 backdrop-blur-md rounded-xl p-6 border border-yellow-500/30"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="mb-4">
        <h3 className="text-xl font-bold text-white flex items-center">
          <span className="mr-2">🎯</span>
          Add Referral Code
        </h3>
        <p className="mt-1 text-sm text-gray-300">
          Were you referred by someone? Add their code to get bonus rates!
        </p>
      </div>

      {!showForm && (
        <motion.button
          onClick={() => setShowForm(true)}
          className="w-full px-4 py-3 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold rounded-lg transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-yellow-300"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          I have a referral code
        </motion.button>
      )}

      {showForm && (
        <motion.div 
          className="space-y-4"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          transition={{ duration: 0.3 }}
        >
          <div>
            <label htmlFor="referralCode" className="block text-sm font-medium text-gray-300 mb-2">
              Enter Referral Code
            </label>
            <div className="flex rounded-lg overflow-hidden">
              <input
                type="text"
                id="referralCode"
                value={referralCode}
                onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                className="flex-1 px-4 py-3 bg-black/30 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="e.g., REFABC123"
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={handleValidate}
                disabled={isValidating || isSubmitting}
                className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-medium transition-colors disabled:opacity-50"
              >
                {isValidating ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white"></div>
                ) : (
                  'Validate'
                )}
              </button>
            </div>
          </div>

          {validationResult && validationResult.valid && (
            <motion.div 
              className="rounded-lg bg-green-500/20 border border-green-500/50 p-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center">
                <span className="text-green-400 text-xl mr-3">✓</span>
                <div>
                  <p className="text-sm font-medium text-green-300">
                    Valid referral code!
                  </p>
                  <p className="text-xs text-green-400">
                    Referred by {validationResult.referrerName}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {error && (
            <motion.div 
              className="rounded-lg bg-red-500/20 border border-red-500/50 p-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center">
                <span className="text-red-400 text-xl mr-3">✗</span>
                <p className="text-sm text-red-300">{error}</p>
              </div>
            </motion.div>
          )}

          {success && (
            <motion.div 
              className="rounded-lg bg-green-500/20 border border-green-500/50 p-4"
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-center">
                <span className="text-green-400 text-xl mr-3">🎉</span>
                <p className="text-sm text-green-300">{success}</p>
              </div>
            </motion.div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!validationResult || !validationResult.valid || isSubmitting}
              className="flex-1 px-4 py-3 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-300"
            >
              {isSubmitting ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                  Processing...
                </div>
              ) : (
                'Add Referral Code'
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setReferralCode('');
                setValidationResult(null);
                setError('');
                setSuccess('');
              }}
              disabled={isSubmitting}
              className="px-4 py-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
            >
              Cancel
            </button>
          </div>

          <div className="mt-4 p-3 bg-blue-500/20 rounded-lg border border-blue-500/30">
            <p className="text-blue-300 text-xs">
              💡 <strong>Note:</strong> After adding a valid referral code, our admin team will process your referral bonus. 
              You'll receive +0.5% bonus on your investment rate once approved.
            </p>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default AddReferralCodeEnhanced;